import { Box } from "@mui/material";
import React, { useEffect, useState, useMemo } from "react";
import { shuffle } from "lodash";
import { CategorySection } from "./components/section-category";
import LoaderPage from "components/layout/LoaderPage";
import useCategoryData from "./hooks/use-category-data.hook";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import RelevantSection from "./components/relevant-section";
import useNewsData from "./hooks/use-news-data.hook";
import { useSourceSearch } from "features/SourceSearch";
import useBoclipsSearch from "./hooks/use-boclips-search.hook";
import useEbscoSearch from "./hooks/use-ebsco-search.hook";
import BoclipsPlayer from "./components/boclips-player";
import { biasRating } from "data/biasRatingLabel";
import { boClipsLabel, ebscoLabel } from "features/SourceSearch/data/search-fields.data";
import { showBoclipsSearch, showEbscoSearch } from "config";

export default function News() {
  const [loading, setLoading] = useState(true);
  const { currentSearchTerm, currentSource } = useSourceSearch();
  const [boclipsSearch, setBoClipsSearch] = useState([]);
  const [ebscoSearch, setEbscoSearch] = useState(null);
  const [loadingStates, setLoadingStates] = useState({});
  const { source, search } = useParams();
  const [tourNext, setTourNext] = useState(null);

  const randomizedBiasRating = useMemo(() => {
    return shuffle([...biasRating]);
  }, [currentSearchTerm]);

  const { fetchNewsCategoryData, categoriesCopy } = useCategoryData(() => { });
  const { combinedSearch, fetchMore } = useNewsData(
    setLoading,
    search,
    categoriesCopy,
    setLoadingStates
  );
  const { getBoclipsSearchData } = useBoclipsSearch(
    setBoClipsSearch,
    currentSearchTerm,
    setLoadingStates
  );
  const { getEbscoSearchData } = useEbscoSearch(
    setEbscoSearch,
    currentSearchTerm,
    setLoadingStates
  );

  const { categories, boclipsPopup } = useSelector((state) => state.data);
  const { isHomeschool } = useSelector((state) => state.account);

  function checkedSource(currentSource) {
    return currentSource == null ? "categories" : currentSource;
  }

  useEffect(() => {
    fetchNewsCategoryData();
  }, [source]);

  useEffect(() => {
    if (((showBoclipsSearch || isHomeschool)) && currentSearchTerm) {
      getBoclipsSearchData(setBoClipsSearch, currentSearchTerm);
    }
  }, [currentSearchTerm]);

  useEffect(() => {
    if (showEbscoSearch && currentSearchTerm) {
      getEbscoSearchData(setEbscoSearch, currentSearchTerm);
    }
  }, [currentSearchTerm]);

  useEffect(() => {
    function triggerAfterDataLoad({ detail }) {
      setTourNext(detail);
    }
    window.addEventListener("tour:app:search:results", triggerAfterDataLoad);
    return () =>
      window.removeEventListener(
        "tour:app:search:results",
        triggerAfterDataLoad
      );
  }, []);

  useEffect(() => {
    if (!loading && tourNext != null) {
      setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent(tourNext.action, { detail: tourNext.index })
        );
        setTourNext(null);
      }, 1000);
    }
  }, [loading, tourNext]);

  if (!categories.length) {
    return (
      <Box
        sx={{
          display: "flex",
          width: "100%",
          height: "100vh",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <LoaderPage />
      </Box>
    );
  }

  const augmentedCategories = [
    ...categories.slice(0, 1),
    ...((showBoclipsSearch || isHomeschool) && currentSource !== "partner-publishers"
      ? [{ category_id: "boclips", categoryName_chr: boClipsLabel }]
      : []),
    ...(showEbscoSearch && currentSource !== "partner-publishers"
      ? [{ category_id: "ebsco", categoryName_chr: ebscoLabel }]
      : []),
    ...categories.slice(1),
  ];

  return (
    <React.Fragment>
      <RelevantSection />
      {(currentSource === "rating" ? randomizedBiasRating : augmentedCategories).map(
        (category, index) => {
          const sectionNews =
            combinedSearch.find(
              (search) => search.media === category.categoryName_chr
            ) || null;

          return (
            <Box
              key={category.category_id}
              id={index === 0 ? "tour-first-cat-section" : undefined}
            >
              <CategorySection
                category={category}
                sectionNews={
                  category.category_id === "boclips"
                    ? boclipsSearch
                    : category.category_id === "ebsco"
                      ? ebscoSearch
                      : sectionNews
                }
                source={checkedSource(source)}
                isLoading={loadingStates[category.categoryName_chr] || loading}
                onFetchMore={fetchMore}
              />
            </Box>
          );
        }
      )}
      {boclipsPopup && <BoclipsPlayer />}
    </React.Fragment>
  );
}
